// @givmo/org-console — ACCESS REQUESTS: the one request table, in two views (shell contract v3,
// T5.5, R4/R9).
//
// ══ ONE COMPONENT, TWO VIEWS ═══════════════════════════════════════════════════════════════════
//
// Mounted without `module`, it is the organization's Team access page's requests section — every
// module's rows, for a holder of `WRITE_MEMBER_ACCESS` — beneath the roster and the invites (the
// ONE order every Team page wears: the people on the team, the people on their way in, the people
// asking). Mounted with `module=<slug>`, it is that module's own Team page's — that module's rows,
// for a holder of `<module>:approve_access_requests` — through the SAME list operation with
// `?module=`. The App column is omitted in the module view because every row's answer would be the
// page's own name, and the heading stays "Access requests" there for the same reason — the module
// is the page's own chrome.
//
// ══ THE GATE READS STRINGS, NEVER A TIER (the build law) ══════════════════════════════════════
//
// Who may see and act here is the caller's SETTLED effective set and capability list off the ONE
// standing read: the org-wide view needs the `WRITE_MEMBER_ACCESS` capability; the module view
// needs that OR the module's approval string. Nothing here asks whether the caller is a Global
// Admin; a Global Admin simply holds the capability. Firing the list on a guess would spend a
// request the server refuses, so the read waits for the answer.
//
// ══ THE ROW STATES ═════════════════════════════════════════════════════════════════════════════
//
//   PENDING, module on          → Approve / Deny
//   PENDING, module off         → "Turn on <app> first" (a link to the Apps page — approve NEVER
//                                  flips the switch; the switch is the one path that presents the
//                                  module's terms and starts its provisioning)
//   PENDING, module setting up  → "Setting up <app> — approve when it is on"
//   APPROVED / DENIED           → a status badge and, for a denial, the note — a record of what was
//                                  decided, not only of what waits (the mirrored table's own rule)
//   VOID                        → "No longer a member", no actions
//
// The module's on/off/setting-up state comes from the caller's own standing rows (the self read
// carries every module's org-wide state), never from a second read.
//
// DATA CONTRACT: the `org-access-requests-*` test-id family — FIXED, not prefixed by the mount,
// because the section names it as a contract; the module view's root is `org-access-requests-<slug>`.

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FOCUS_RING,
  Icon,
  cn,
  shouldInterceptClick,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import { Inbox } from 'lucide-react';
import type { OrgConsoleConfig } from './org-console-config';
import { isOrgConsoleUnavailable } from './org-console-transport';
import { accessRequesterName, formatOrgEventTime } from './org-decode';
import { standingsFromModuleRows, type ModuleStanding } from './module-verdict';
import { ROLE_OWNER, moduleTier, moduleTierLabel, type OrgTier } from './org-tiers';
import { ApproveAccessRequestDialog, DenyAccessRequestDialog } from './org-access-request-dialogs';
import {
  OrgConsoleUnavailableNotice,
  OrgNotPermittedNotice,
  OrgTierUnknownNotice,
} from './org-console-notices';
import {
  CAPABILITY_WRITE_MEMBER_ACCESS,
  approveAccessRequestsPermission,
  holdsCapability,
} from './standing-read';
import {
  useApproveOrgAccessRequest,
  useDenyOrgAccessRequest,
  useOrgAccessGate,
  useOrgAccessRequests,
  useOrgMembers,
} from './use-org-console';
import { ExternalNote } from './org-external';
import { TEAM_COPY } from './org-team-copy';
import type { OrgAccessRequestRow, OrgAccessRequestStatus } from './wire-types';

/** The family is a CONTRACT — see the header. */
export const ORG_ACCESS_REQUESTS_TEST_ID = 'org-access-requests';

export interface OrgAccessRequestsPanelProps {
  config: OrgConsoleConfig;
  /** The module view: only that module's rows, no App column. Absent ⇒ the org-wide view. */
  module?: string | null;
  /** The host's page already carries this title: render it for assistive technology alone. */
  headingHidden?: boolean;
}

/** The status word a decided row carries. */
export function accessRequestStatusLabel(status: OrgAccessRequestStatus): string {
  switch (status) {
    case 'APPROVED':
      return 'Approved';
    case 'DENIED':
      return 'Declined';
    case 'VOID':
      return 'No longer a member';
    case 'PENDING':
      return 'Pending';
  }
}

/** The module's ORG-WIDE state for a row, read off the caller's own standing rows. */
function moduleState(
  standing: ModuleStanding | undefined,
): 'on' | 'off' | 'setting-up' | 'unknown' {
  if (standing === undefined) return 'unknown';
  if (standing.activationState === 'off') return 'off';
  if (standing.activationState === 'setting-up') return 'setting-up';
  if (standing.activationState !== 'on' && !standing.orgEnabled) return 'off';
  return 'on';
}

const EXPLANATION_CLIP = 64;

/**
 * The server's refusals, in words — detected on the CODE (PR-BE1 wire note, `c4ea77664`), never
 * on the server's English. A 409 names which door is in the way; the generic sentence covers the
 * rest, and the dialog stays open either way.
 */
function refusalCopy(error: unknown): string {
  if (isGivmoError(error)) {
    const code = firstErrorCode(error);
    if (error.status === 409) {
      if (code === 'MODULE_NOT_ENABLED')
        return 'Turn the app on first — approving never flips the switch.';
      if (code === 'REQUESTER_NOT_A_MEMBER')
        return 'This person is no longer on the team, so there is nothing to approve.';
      if (code === 'REQUEST_NOT_PENDING') return 'This request was already decided.';
      return 'This request can’t be decided right now.';
    }
    if (error.status === 403) return 'You can’t decide this request.';
  }
  return 'We couldn’t save that. Nothing was changed — please try again.';
}

type OpenDialog = { mode: 'approve' | 'deny'; request: OrgAccessRequestRow } | null;

export function OrgAccessRequestsPanel({
  config,
  module = null,
  headingHidden = false,
}: OrgAccessRequestsPanelProps) {
  const root =
    module === null ? ORG_ACCESS_REQUESTS_TEST_ID : `${ORG_ACCESS_REQUESTS_TEST_ID}-${module}`;
  const rowPrefix = ORG_ACCESS_REQUESTS_TEST_ID;
  const gate = useOrgAccessGate(config.orgRef);
  const self = gate.self;

  const standings = useMemo(() => standingsFromModuleRows(self?.modules), [self]);

  // ── The gate: strings and capabilities, never a tier ──────────────────────────────────────
  //
  // The module view admits on the module's approval string alone. (Until EP-WEB36-B it also read
  // the standing row's count, against a backend that masked it by the retired `global_admins_only`
  // approver setting and refused that Owner's list 403; no backend that can serve does since
  // EP-WEB36, D-EP-58. A refusal still renders as the calm notice below.)
  const approvesAll = holdsCapability(self?.capabilities ?? null, CAPABILITY_WRITE_MEMBER_ACCESS);
  const approvesModule =
    module !== null && (self?.permissions ?? []).includes(approveAccessRequestsPermission(module));
  const settled = self !== null;
  const allowed = settled && (approvesAll || approvesModule);

  const list = useOrgAccessRequests(config.orgRef, module, allowed);
  // Who the roster marks External (§OM-53): whether the person asking is from outside is exactly what
  // an approver weighs. The page's own roster already makes this read, so the cache shares it; best
  // effort, and a row simply carries no note until it lands. Never a gate: it changes no control.
  const roster = useOrgMembers(config.orgRef);
  const externalIds = useMemo(
    () => new Set((roster.data ?? []).filter((member) => member.external).map((m) => m.user_id)),
    [roster.data],
  );
  const approve = useApproveOrgAccessRequest(config.orgRef, config.onAccessWritten);
  const deny = useDenyOrgAccessRequest(config.orgRef);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  // The belt behind the admission: a 403 from the list is the server saying no, and it renders as
  // the same calm statement — never as the generic failure with a retry that cannot succeed.
  const refused = list.isError && isGivmoError(list.error) && list.error.status === 403;
  const rows = useMemo(() => list.data ?? [], [list.data]);
  const pendingCount = rows.filter((row) => row.status === 'PENDING').length;
  const label = (slug: string) => config.vocab.moduleLabel(slug);
  const appsHref = config.links?.apps;

  const closeDialog = () => {
    setDialog(null);
    approve.reset();
    deny.reset();
  };

  const columns: DataTableColumn<OrgAccessRequestRow>[] = useMemo(() => {
    const base: DataTableColumn<OrgAccessRequestRow>[] = [
      {
        id: 'requester',
        header: 'Requester',
        width: module === null ? '26%' : '32%',
        cell: (row) => {
          const name = accessRequesterName(row);
          return (
            <div className="min-w-0">
              <div className="flex min-w-0 items-baseline gap-8">
                <div className="truncate font-w600 text-text-strong">{name}</div>
                {externalIds.has(row.requester_user_id) && (
                  <ExternalNote testId={`${rowPrefix}-row-${row.id}-external`} />
                )}
              </div>
              {/* A requester whose account carries no name is named by their address, so it is
                  said once, never again on the line beneath (the roster's rule, OM-79). */}
              {row.requester_email !== null && row.requester_email !== name && (
                <div className="truncate text-s13 text-text-tertiary">{row.requester_email}</div>
              )}
            </div>
          );
        },
      },
    ];
    if (module === null) {
      base.push({
        id: 'app',
        header: 'App',
        stacked: 'meta' as const,
        cell: (row) => (
          <span className="text-text-secondary" {...testIdProp(`${rowPrefix}-row-${row.id}-app`)}>
            {label(row.module)}
          </span>
        ),
      });
    }
    base.push(
      {
        id: 'role',
        header: 'Role requested',
        stacked: 'meta' as const,
        cell: (row) => (
          <span {...testIdProp(`${rowPrefix}-row-${row.id}-role`)}>
            <Badge tone={moduleTier(row.requested_role) === ROLE_OWNER ? 'brand' : 'neutral'}>
              {moduleTierLabel(row.requested_role)}
            </Badge>
          </span>
        ),
      },
      {
        id: 'explanation',
        header: 'Explanation',
        width: '30%',
        stacked: 'line' as const,
        cell: (row) => {
          if (row.explanation === null) return <span className="text-text-placeholder">—</span>;
          const long = row.explanation.length > EXPLANATION_CLIP;
          const open = expanded === row.id;
          const text =
            long && !open
              ? `${row.explanation.slice(0, EXPLANATION_CLIP).trimEnd()}…`
              : row.explanation;
          return (
            <span
              className="text-s13 text-text-secondary"
              {...testIdProp(`${rowPrefix}-row-${row.id}-explanation`)}
            >
              “{text}”
              {long && (
                <button
                  type="button"
                  onClick={() => setExpanded(open ? null : row.id)}
                  aria-expanded={open}
                  className={cn(
                    'ml-6 rounded-sm font-w600 text-brand-600 outline-none hover:text-brand-700',
                    // The 44px phone floor: padding grows the hit area, negative margins keep the
                    // inline flow where it was, and `relative` lifts the padded box above the
                    // neighbouring lines' text so the whole box is hit-testable.
                    'max-sm:relative max-sm:inline-block max-sm:-my-12 max-sm:-ml-2 max-sm:-mr-8 max-sm:px-8 max-sm:py-12',
                    FOCUS_RING,
                  )}
                  {...testIdProp(`${rowPrefix}-row-${row.id}-explanation-toggle`)}
                >
                  {open ? 'Less' : 'More'}
                </button>
              )}
            </span>
          );
        },
      },
      {
        id: 'requested',
        header: 'Requested',
        stacked: 'line' as const,
        stackedLabel: 'Requested',
        cell: (row) => (
          <span className="whitespace-nowrap text-text-tertiary tabular-nums">
            {formatOrgEventTime(row.created_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        align: 'right' as const,
        width: '240px',
        stacked: 'line' as const,
        cell: (row) => {
          if (row.status !== 'PENDING') {
            return (
              <span className="inline-flex flex-col items-end gap-4 max-sm:items-start">
                <Badge
                  tone={row.status === 'APPROVED' ? 'success' : 'neutral'}
                  testId={`${rowPrefix}-row-${row.id}-status`}
                >
                  {accessRequestStatusLabel(row.status)}
                </Badge>
                {row.resolution_note !== null && (
                  <span
                    className="text-s12 text-text-tertiary"
                    {...testIdProp(`${rowPrefix}-row-${row.id}-note`)}
                  >
                    “{row.resolution_note}”
                  </span>
                )}
              </span>
            );
          }
          const state = moduleState(standings?.[row.module]);
          if (state === 'off') {
            // Approve NEVER flips the org switch (T5.3) — the door is the Apps page, when routed.
            const text = `Turn on ${label(row.module)} first`;
            if (appsHref === undefined) {
              return (
                <span
                  className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary"
                  {...testIdProp(`${rowPrefix}-row-${row.id}-turn-on-first`)}
                >
                  {text}
                </span>
              );
            }
            return (
              <a
                href={appsHref}
                onClick={(event) => {
                  if (config.onNavigate === undefined || !shouldInterceptClick(event)) return;
                  event.preventDefault();
                  config.onNavigate(appsHref);
                }}
                className={cn(
                  'rounded-sm font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700',
                  FOCUS_RING,
                )}
                {...testIdProp(`${rowPrefix}-row-${row.id}-turn-on-first`)}
              >
                {text}
              </a>
            );
          }
          if (state === 'setting-up') {
            return (
              <span
                className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary"
                {...testIdProp(`${rowPrefix}-row-${row.id}-setting-up`)}
              >
                Setting up {label(row.module)} — approve when it is on
              </span>
            );
          }
          return (
            <div className="flex items-center justify-end gap-8 max-sm:justify-start">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                className="max-sm:h-44 max-sm:px-16"
                onClick={() => setDialog({ mode: 'deny', request: row })}
                testId={`${rowPrefix}-row-${row.id}-deny`}
              >
                Deny
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                className="max-sm:h-44 max-sm:px-16"
                onClick={() => setDialog({ mode: 'approve', request: row })}
                testId={`${rowPrefix}-row-${row.id}-approve`}
              >
                Approve
              </Button>
            </div>
          );
        },
      },
    );
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `label` reads a stable vocab
  }, [
    module,
    expanded,
    standings,
    appsHref,
    config.onNavigate,
    config.vocab,
    rowPrefix,
    externalIds,
  ]);

  if (gate.isUnavailable || (list.isError && isOrgConsoleUnavailable(list.error))) {
    return (
      <OrgConsoleUnavailableNotice
        testId={`${root}-unavailable`}
        title="Access requests"
        description="This environment doesn’t have the console turned on, so there are no requests to show yet."
      />
    );
  }

  if (gate.isPending) {
    return (
      <p
        role="status"
        className="font-sans text-s13 text-text-tertiary"
        {...testIdProp(`${root}-loading`)}
      >
        Loading…
      </p>
    );
  }

  if (gate.tierUnknown) {
    return <OrgTierUnknownNotice testId={`${root}-tier-retry`} onRetry={gate.refetch} />;
  }

  if (!allowed || refused) {
    return (
      <OrgNotPermittedNotice
        testId={`${root}-not-permitted`}
        title="Only an approver can see this"
        description={
          module === null
            ? `Ask a Global Admin at your ${config.vocab.orgNoun} to review access requests.`
            : `Ask a Global Admin, or an ${moduleTierLabel(ROLE_OWNER)} of ${label(module)}, to review these requests.`
        }
      />
    );
  }

  // "Access requests" in BOTH views: on a module's Team page the module is already the page's own
  // chrome (the sidebar row, the breadcrumb, the page header), so a heading that named it again
  // said one thing twice — Jonathan, at his 2026-09-04 console walkthrough ("the section title should
  // just be Access requests, not Donations access requests"). The description beneath and the
  // not-permitted notice still say which app.
  const heading = 'Access requests';
  const requester = dialog === null ? '' : accessRequesterName(dialog.request);
  const appLabel = dialog === null ? '' : label(dialog.request.module);

  return (
    <section className="flex flex-col gap-12" {...testIdProp(root)}>
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="min-w-0">
          <h2
            className={
              headingHidden
                ? 'sr-only'
                : 'font-sans text-s16 font-w700 tracking-givmo text-text-strong'
            }
          >
            {heading}
          </h2>
          <p className="mt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {/* Team access and an app's own page say it the same way (D-CPO-77). */}
            {TEAM_COPY.requestsLede(module === null ? null : label(module))}
          </p>
        </div>
        {pendingCount > 0 && (
          <Badge tone="brand" testId={`${root}-pending-badge`}>
            {pendingCount} pending
          </Badge>
        )}
      </div>

      <DataTable
        testId={`${root}-list`}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        rowTestId={() => `${rowPrefix}-row`}
        // Cards below `lg` — the seam the sidebar becomes a drawer at, and the one that actually
        // covers a 768px tablet (`max-md` is `< 768px`): six columns plus the actions do not fit
        // there, and a table whose Approve/Deny sit past its own scroll edge hides the page's
        // only acts.
        stackAt="lg"
        loadingLabel="Loading access requests…"
        loading={list.isPending}
        error={list.isError ? 'We couldn’t load the access requests.' : undefined}
        onRetry={() => void list.refetch()}
        emptyState={
          <EmptyState
            testId={`${root}-empty-state`}
            icon={<Icon icon={Inbox} size={20} aria-hidden />}
            title="No one is asking for access"
            description="When someone asks to open an app you can approve, it shows up here."
          />
        }
        footer={
          list.hasNextPage ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              disabled={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
              testId={`${root}-load-more`}
            >
              {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          ) : null
        }
      />

      <ApproveAccessRequestDialog
        open={dialog?.mode === 'approve'}
        onOpenChange={(open) => !open && closeDialog()}
        requesterName={requester}
        appLabel={appLabel}
        requestedRole={dialog?.request.requested_role ?? ''}
        pending={approve.isPending}
        error={approve.isError ? refusalCopy(approve.error) : null}
        onApprove={(grantedRole: OrgTier) => {
          if (dialog === null) return;
          approve.mutate(
            { requestId: dialog.request.id, body: { granted_role: grantedRole } },
            { onSuccess: () => closeDialog() },
          );
        }}
        testIdPrefix={rowPrefix}
      />
      <DenyAccessRequestDialog
        open={dialog?.mode === 'deny'}
        onOpenChange={(open) => !open && closeDialog()}
        requesterName={requester}
        appLabel={appLabel}
        pending={deny.isPending}
        error={deny.isError ? refusalCopy(deny.error) : null}
        onDeny={(note) => {
          if (dialog === null) return;
          deny.mutate(
            { requestId: dialog.request.id, body: note.length > 0 ? { note } : {} },
            { onSuccess: () => closeDialog() },
          );
        }}
        testIdPrefix={rowPrefix}
      />
    </section>
  );
}
